import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { Col, Form, message, Row, Typography } from 'antd';
import { useUpdateCurrentOwnerInfoMutation } from 'gql/types';
import { Button, Spin } from 'modules/common/components';
import { UnsavedChangesModal } from 'modules/common/components/UnsavedChangesModal';
import { useUnsavedChangesWarning } from 'modules/common/hooks/useUnsavedChangesWarning';
import { GET_CURRENT_USER_QUERY } from 'modules/user/graphql/queries/user';
import { useCurrentUser } from 'modules/user/hooks/useCurrentUser';
import { FC, useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AdditionalContactsSection from './AdditionalContactsSection';
import PersonalInfoSection from './PersonalInfoSection';
import { EditProfileFormSchemaType, editProfileFormSchema } from './schema';
import styles from './styles.module.scss';

const { Title } = Typography;

const EditProfileForm: FC = () => {
  const { t } = useTranslation('owner.EditProfileForm');

  const currentUser = useCurrentUser();
  const userContact = currentUser.data?.contacts![0];

  const [isEditMode, setIsEditMode] = useState(false);

  const editProfileFrom = useForm<EditProfileFormSchemaType>({
    resolver: yupResolver(editProfileFormSchema),
    mode: 'onChange',
  });

  const {
    formState: { isDirty, isValid },
    handleSubmit,
    reset,
    trigger,
    getFieldState,
    getValues,
  } = editProfileFrom;

  const [updateOwnerInfo, updateOwnerInfoMutation] =
    useUpdateCurrentOwnerInfoMutation({
      refetchQueries: ['currentUser'],
      onCompleted: () => {
        message.success(t('saved'));
        setIsEditMode(false);
      },
      onError: (error) => message.error(error.message),
    });

  useEffect(() => {
    resetValues();
  }, [currentUser]);

  const isLoading = updateOwnerInfoMutation.loading;

  const resetValues = () => {
    reset({
      personalInfo: {
        firstName: currentUser.data?.firstName,
        lastName: currentUser.data?.lastName,
        phoneNumber: currentUser.data?.phoneNumber,
        streetAddress: currentUser.data?.streetAddress,
        city: currentUser.data?.city,
        state: currentUser.data?.state,
        zipCode: currentUser.data?.zipCode,
        locationPreferences: currentUser.data?.locationPreferences,
        birthday: currentUser.data?.birthday
          ? new Date(currentUser.data?.birthday)
          : undefined,
        contactPreferences: currentUser.data?.contactPreferences,
      },
      contact: {
        firstName: userContact?.firstName,
        lastName: userContact?.lastName,
        email: userContact?.email,
        streetAddress: userContact?.streetAddress,
        city: userContact?.city,
        state: userContact?.state,
        zipCode: userContact?.zipCode,
        phoneNumber: userContact?.phoneNumber,
      },
    });
  };

  const closeHandler = () => {
    setIsEditMode(false);
    resetValues();
  };

  const editHandler = () => {
    trigger();
    setIsEditMode(true);
  };

  const submitForm = async (data: EditProfileFormSchemaType) => {
    const { contact, personalInfo } = data;
    const personalInfoState = getFieldState('personalInfo');
    const contactState = getFieldState('contact');

    if (personalInfoState.isDirty || contactState.isDirty) {
      updateOwnerInfo({
        refetchQueries: [GET_CURRENT_USER_QUERY],
        variables: {
          input: {
            id: currentUser.data!.id,
            ...personalInfo,
            additionalContact: {
              ...contact,
              id: userContact?.id,
            },
          },
        },
      });
    }
  };

  const saveHandler = handleSubmit(submitForm);

  const handleSave = async () => {
    const formData = getValues();
    await submitForm(formData);
  };

  const {
    isModalOpen,
    handleStay,
    handleDiscard,
    handleSaveAndLeave,
    canSaveAndLeave,
  } = useUnsavedChangesWarning({
    isDirty,
    onSave: handleSave,
    isFormValid: isValid,
  });

  return (
    <>
      <Spin spinning={isLoading}>
        <FormProvider {...editProfileFrom}>
          <Row>
            <Col span={24}>
              <Form layout="vertical">
                <Row justify="space-between">
                  <Col xs={24} md={12}>
                    <Title level={4} className={styles.sectionTitle}>
                      {t('personalInfoSectionTitle')}
                    </Title>
                    <PersonalInfoSection disabled={!isEditMode} />
                  </Col>

                  <Col xs={24} md={12}>
                    <Title level={4} className={styles.sectionTitle}>
                      {t('additionalContactsSectionTitle')}
                    </Title>
                    <AdditionalContactsSection disabled={!isEditMode} />
                  </Col>
                </Row>
              </Form>
            </Col>
            <Col span={24}>
              <Row justify="space-between">
                <Col>
                  <Button onClick={closeHandler} disabled={!isEditMode}>
                    {t('close')}
                  </Button>
                </Col>
                <Col>
                  {isEditMode ? (
                    <Button
                      type="primary"
                      disabled={!isValid}
                      onClick={saveHandler}
                    >
                      {t('save')}
                    </Button>
                  ) : (
                    <Button onClick={editHandler} type="primary">
                      {t('edit')}
                    </Button>
                  )}
                </Col>
              </Row>
            </Col>
          </Row>
        </FormProvider>
      </Spin>

      <UnsavedChangesModal
        isOpen={isModalOpen}
        onStay={handleStay}
        onDiscard={handleDiscard}
        onSaveAndLeave={handleSaveAndLeave}
        canSaveAndLeave={canSaveAndLeave}
        isSaving={isLoading}
      />
    </>
  );
};

export default EditProfileForm;
